// SplatLoadingOverlay — UX feedback while a gsplat environment downloads
// and parses. Without this the screen goes black after pressing "Apply
// environment" with no signal that anything is happening, then the splat
// pops in seconds later — confusing.
//
// Visible whenever:
//   - liveTuneStore.environment.splatUrl is non-null AND
//   - window.__SPLAT_LOADED__ is not true (i.e. SplatMesh.onLoad hasn't fired)
//
// Mounted as a sibling of <Canvas> in App.jsx so it overlays the WebGL
// surface. Lightweight: a single fixed-position pill in the lower-center
// with a spinner + filename + size hint.

import React, { useEffect, useRef, useState } from 'react'
import { liveTuneStore } from '../lib/liveTuneStore'
import { CustomAssetManager } from '../lib/CustomAssetManager'
import { fmtSize } from './LoadProgressCard.jsx'

const POLL_MS = 200
// How long the parse % can sit *without advancing* before we flag the decode
// as stalled. Spark reports real decode progress via onProgress, so a slow but
// steadily-advancing decode stays "healthy"; only a frozen bar trips the
// "reload to retry" escape hatch.
const DECODE_WARN_MS = 20000

export default function SplatLoadingOverlay() {
  const [splatUrl, setSplatUrl] = useState(liveTuneStore.getState().environment.splatUrl)
  const [loaded, setLoaded] = useState(() =>
    typeof window !== 'undefined' && window.__SPLAT_LOADED__ === true,
  )
  // Only show while a fetch is actually in flight (SplatEnvironment sets
  // this true on mount, false on unmount). Prevents a stuck "0% / 125 MB"
  // overlay on page refresh when liveTuneStore.environment.splatUrl is
  // restored from localStorage but the app boots in edit mode (no
  // SplatEnvironment mounted, no fetch started).
  const [loadStarted, setLoadStarted] = useState(() =>
    typeof window !== 'undefined' && window.__SPLAT_LOAD_STARTED__ === true,
  )
  const [progress, setProgress] = useState(0)        // 0..1, or -1 = indeterminate (download)
  const [decodeProgress, setDecodeProgress] = useState(0) // 0..1, or -1 = indeterminate (parse)
  const [bytes, setBytes] = useState({ received: 0, total: 0 })
  const [error, setError] = useState(null)
  // Decode-phase liveness. Spark reports real parse progress via onProgress
  // (→ decodeProgress), so we show a determinate bar. The "may be stuck"
  // warning keys off a *stall* (no % advance for DECODE_WARN_MS), not raw
  // elapsed time — a slow-but-advancing decode is healthy, not broken.
  const [decodeStartedAt, setDecodeStartedAt] = useState(null)
  const [stalledMs, setStalledMs] = useState(0)
  const [now, setNow] = useState(() => Date.now())
  const decProgRef = useRef(0)
  const lastAdvanceRef = useRef(Date.now())

  // Subscribe to liveTuneStore so URL changes are reflected immediately
  // (we don't have to wait for the next poll tick to know the user picked
  // a new env). NOTE: this store's subscribe(fn) calls fn() with no args —
  // we have to pull the snapshot via getState() inside the callback.
  useEffect(() => {
    const unsub = liveTuneStore.subscribe(() => {
      setSplatUrl(liveTuneStore.getState().environment.splatUrl)
    })
    return unsub
  }, [])

  // __SPLAT_LOADED__ / __SPLAT_LOAD_PROGRESS__ are plain window globals
  // written from inside SplatEnvironment's effect — no event bus to
  // subscribe to, so we poll. Cheap (150ms) and only matters during
  // the brief load window.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const id = setInterval(() => {
      const done = window.__SPLAT_LOADED__ === true
      const started = window.__SPLAT_LOAD_STARTED__ === true
      const prog = typeof window.__SPLAT_LOAD_PROGRESS__ === 'number' ? window.__SPLAT_LOAD_PROGRESS__ : 0
      const decProg = typeof window.__SPLAT_DECODE_PROGRESS__ === 'number' ? window.__SPLAT_DECODE_PROGRESS__ : 0
      const err = window.__SPLAT_LOAD_ERROR__ ?? null
      setLoaded(done)
      setLoadStarted(started)
      setProgress(prog)
      setDecodeProgress(decProg)
      setBytes({
        received: Number(window.__SPLAT_BYTES_RECEIVED__ ?? 0),
        total:    Number(window.__SPLAT_BYTES_TOTAL__ ?? 0),
      })
      setError(err)
      const t = Date.now()
      setNow(t)
      // Decode phase = download complete, not yet loaded, no error.
      const decoding = started && !done && !err && prog >= 1
      // Reset the stall timer when not decoding or whenever the parse %
      // advances; otherwise let it accumulate so a frozen decode surfaces.
      if (!decoding || decProg > decProgRef.current + 0.001) {
        decProgRef.current = decProg
        lastAdvanceRef.current = t
      }
      setDecodeStartedAt(prev => decoding ? (prev ?? t) : null)
      setStalledMs(decoding ? t - lastAdvanceRef.current : 0)
    }, POLL_MS)
    return () => clearInterval(id)
  }, [])

  // Three conditions must hold to show the overlay:
  //   1. there's a target URL (otherwise nothing to load)
  //   2. SplatEnvironment is actively trying to load it (not just a stale
  //      localStorage URL on a refreshed edit-mode boot)
  //   3. the load hasn't finished yet
  if (!splatUrl || !loadStarted || loaded) return null

  // Try to surface the asset name + size if we recognize the URL — fall
  // back to the URL tail otherwise. CAM owns the env list for the active
  // character; lookups by file_url are O(N) but N is small.
  const meta = (CustomAssetManager.listEnvironmentAssets() ?? []).find(
    e => (e.fileUrl ?? e.file_url) === splatUrl,
  )
  const label = meta?.name ?? splatUrl.split('/').pop()
  const sizeHint = meta?.byte_size ?? meta?.byteSize
    ? fmtSize(meta.byte_size ?? meta.byteSize)
    : null

  return (
    <>
      {/* Dim backdrop — semi-transparent so the user sees the canvas still
          working underneath, but loud enough to read as "wait, something
          is happening." Pointer-events:none so it doesn't capture clicks. */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'radial-gradient(circle at center, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 60%, transparent 100%)',
        zIndex: 29, pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute',
        left: '50%', top: '50%',
        transform: 'translate(-50%, -50%)',
        background: 'rgba(12, 18, 30, 0.94)',
        border: '1px solid #8b6cff',
        borderRadius: 10,
        padding: '20px 28px',
        color: '#e1e1ed',
        fontFamily: 'var(--m-font)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
        zIndex: 30, pointerEvents: 'none',
        boxShadow: '0 12px 40px rgba(0,0,0,.65), 0 0 0 1px rgba(139,108,255,.12), 0 0 32px rgba(139,108,255,.18)',
        minWidth: 320, maxWidth: 'min(90%, 520px)',
        textAlign: 'center',
      }}>
        {/* Stage: pre-download → download → parse. We only know real %
            during download; after that we show "Parsing…" with a spinner
            since Spark doesn't expose parse progress. */}
        {error ? (
          <div style={{ color: '#f87171', fontSize: 20 }}>⚠</div>
        ) : (
          <div
            aria-hidden
            style={{
              width: 40, height: 40,
              borderRadius: '50%',
              border: '4px solid #8b6cff22',
              borderTopColor: '#8b6cff',
              animation: 'aos-splat-spin 0.9s linear infinite',
            }}
          />
        )}
        <div style={{ color: error ? '#f87171' : '#8b6cff', fontSize: 13, letterSpacing: '.08em', textTransform: 'uppercase' }}>
          {error ? 'Environment failed to load' : (progress >= 1 ? 'Parsing environment' : 'Loading environment')}
        </div>
        <div style={{
          color: '#e1e1ed', fontSize: 15, fontWeight: 'bold',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          maxWidth: '100%',
        }}>
          {label}
        </div>

        {!error && progress < 1 && (
          <div style={{ width: '100%', marginTop: 4 }}>
            {/* Progress bar: determinate if we know total bytes,
                indeterminate (sliding stripe) otherwise. */}
            <div style={{
              width: '100%', height: 8,
              background: '#0d1622',
              border: '1px solid #8b6cff33',
              borderRadius: 4,
              overflow: 'hidden',
              position: 'relative',
            }}>
              {progress >= 0 ? (
                <div style={{
                  width: `${Math.max(2, Math.round(progress * 100))}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #6c4cff, #8b6cff)',
                  transition: 'width 0.15s linear',
                }} />
              ) : (
                <div style={{
                  position: 'absolute', inset: 0,
                  background: 'linear-gradient(90deg, transparent 0%, #8b6cff 50%, transparent 100%)',
                  backgroundSize: '40% 100%',
                  animation: 'aos-splat-stripe 1.2s linear infinite',
                }} />
              )}
            </div>
            <div style={{ marginTop: 6, color: '#8f8fa2', fontSize: 11, display: 'flex', justifyContent: 'space-between' }}>
              <span>
                {progress >= 0
                  ? `${Math.round(progress * 100)}%`
                  : 'downloading…'}
              </span>
              <span>
                {bytes.total > 0
                  ? `${fmtSize(bytes.received)} / ${fmtSize(bytes.total)}`
                  : (bytes.received > 0 ? fmtSize(bytes.received) : (sizeHint ?? ''))}
              </span>
            </div>
          </div>
        )}

        {!error && progress >= 1 && (() => {
          const elapsedS = decodeStartedAt ? Math.floor((now - decodeStartedAt) / 1000) : 0
          const stuck = stalledMs >= DECODE_WARN_MS
          const pct = Math.round(decodeProgress * 100)
          return (
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <div style={{ width: '100%' }}>
                {/* Real parse % from Spark's decode worker. Indeterminate
                    stripe only when the file reports no total. */}
                <div style={{
                  width: '100%', height: 8,
                  background: '#0d1622',
                  border: '1px solid #8b6cff33',
                  borderRadius: 4,
                  overflow: 'hidden',
                  position: 'relative',
                }}>
                  {decodeProgress >= 0 ? (
                    <div style={{
                      width: `${Math.max(2, pct)}%`,
                      height: '100%',
                      background: stuck
                        ? 'linear-gradient(90deg, #b8860b, #fbbf24)'
                        : 'linear-gradient(90deg, #6c4cff, #8b6cff)',
                      transition: 'width 0.15s linear',
                    }} />
                  ) : (
                    <div style={{
                      position: 'absolute', inset: 0,
                      background: 'linear-gradient(90deg, transparent 0%, #8b6cff 50%, transparent 100%)',
                      backgroundSize: '40% 100%',
                      animation: 'aos-splat-stripe 1.2s linear infinite',
                    }} />
                  )}
                </div>
                <div style={{ marginTop: 6, color: '#8f8fa2', fontSize: 11, display: 'flex', justifyContent: 'space-between' }}>
                  <span>{decodeProgress >= 0 ? `decoding ${pct}%` : 'decoding splats…'}</span>
                  <span>{elapsedS}s</span>
                </div>
              </div>
              {stuck && (
                <div style={{ color: '#fbbf24', fontSize: 11, maxWidth: 300, lineHeight: 1.4 }}>
                  Decode appears stalled (no progress for {Math.floor(stalledMs / 1000)}s).
                  Reload the page (Ctrl/Cmd+R) to retry.
                </div>
              )}
            </div>
          )
        })()}

        {error && (
          <div style={{ color: '#f87171', fontSize: 11, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {error}
          </div>
        )}

        <style>{`
          @keyframes aos-splat-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          @keyframes aos-splat-stripe { from { background-position: -40% 0; } to { background-position: 140% 0; } }
        `}</style>
      </div>
    </>
  )
}
